import React, { useState } from "react";
import SectionHeading from "components/SectionHeading";
import sharedClasses from "features/Model/sharedStyles.module.scss";
import classes from "../style.module.scss";
import classNames from "classnames";

import SettingsIcon from "assets/images/icons/JSX/Settings";

import ParametricUpgradeIcon from "assets/images/icons/ParametricUpgradeIcons.svg?react";
import ParametricPremadeIcon from "assets/images/icons/ParametricPremadeIcon.svg?react";

import Accordion from "components/Accordion";
// import { APP_VERSION } from "utils/constants/version";

//TODO: - Add the credit icon wherever credits are mentioned.
//TODO: put this link over OS-HPXML: https://github.com/NatLabRockies/OpenStudio-HPXML
export default () => {
    return (
        <div>
            <SectionHeading title="Product Updates" subtitle="Check here for the latest updates and new releases." />
            <div className={classes.contentWrapper}>
                <div className={classes.row}>
                    <h3>Update 1.1.0</h3>
                    <h4>October 2026</h4>
                    <p>
                        <strong>
                            <i>Reference houses, hybrid heating, single zone cooling, and organization updates.</i>
                        </strong>
                    </p>
                    <p>
                        This update adds code reference houses, a General-mode SOC approximation, and updates to the
                        organization system. We've also added two new Insights features: hybrid heating and single-zone
                        cooling analysis, and a new Community Analysis parametric system.
                    </p>
                    <ul>
                        <li>
                            <strong>Reference Houses</strong>:
                        </li>
                        <ul>
                            <li>
                                Build an NBC 2015, NBC 2020, NBC 2025, or Ontario SB-12 reference house from the
                                proposed house. Add a reference house as a package in the Upgrades table,
                            </li>
                            <li>
                                Add a reference house as a package in the Upgrades table and compare it to the proposed
                                house or other design options.
                            </li>
                            <li>
                                The model owner can also replace the saved file with the reference house from Model
                                Settings. That sets the file to General mode, deletes every upgrade package, and
                                detaches current results. Duplicate the model first if you need to keep the proposed
                                house.
                            </li>
                            <li>
                                Reference house packages can be exported from the Review tab, so all of the contents of
                                the file can be reviewed.
                            </li>
                            <li>
                                How each reference house is built is described on the new{" "}
                                <a href="/documentation/reference-houses">Reference Houses</a> documentation page in the
                                header.
                            </li>
                        </ul>
                    </ul>
                    <ul>
                        <li>
                            <strong>SOC Approximation</strong>:
                        </li>
                        <ul>
                            <li>
                                General-mode files can now approximate standard operating conditions. Turn this
                                capability on under Operating conditions in Model Settings. Saved inputs stay as
                                entered. Simulations use an altered copy of the file.
                            </li>
                            <li>
                                The approximation is for quick comparisons. Its accuracy against native standard
                                operating conditions is described on the new{" "}
                                <a href="/documentation/soc-approximation">SOC Approximation</a> documentation page in
                                the header.
                            </li>
                        </ul>
                    </ul>
                    <ul>
                        <li>
                            <strong>Hybrid Heating Analysis</strong> (Insights):
                        </li>
                        <ul>
                            <li>
                                A new Hybrid Heating Analysis feature in Insights uses EnergyPlus to analyze the
                                performance of a hybrid heating system with an air-source heat pump and a fossil-fuel
                                backup.
                            </li>
                            <li>
                                The analysis compares performance across switchover temperatures, so you can pick the
                                right switchover temperature for your system. Results include energy, cost, and
                                emissions, and show how the cost-optimal switchover changes with electricity and
                                backup-fuel prices.
                            </li>
                            <li>
                                Enter effective fuel prices or detailed time-of-use prices, and perform a price
                                sensitivity analysis to see how the cost-optimal switchover changes with different fuel
                                prices.
                            </li>
                            <li>
                                Hybrid Heating is free until April 2027. After that, activate Insights (1 credit) for
                                permanent access, alongside room-by-room analysis, EnergyPlus, Utility Analysis, and
                                Single Zone Cooling.
                            </li>
                        </ul>
                    </ul>
                    <ul>
                        <li>
                            <strong>Single Zone Cooling</strong> (Insights):
                        </li>
                        <ul>
                            <li>
                                A new Single Zone Cooling feature in Insights estimates the cooling load for a defined
                                zone of a dwelling.
                            </li>
                            <li>
                                A checklist follows the Guideline on Single Zone Cooling in Dwelling Units to guide you
                                through the process of defining a zone.
                            </li>
                            <li>
                                Results show each zone&apos;s sensible heat gains: exterior and interior conduction,
                                occupants, base loads, air change, and ventilation, plus the total sensible and latent
                                gain.
                            </li>
                            <li>
                                The model needs a complete room-by-room analysis before the single zone cooling analysis
                                can run.
                            </li>
                        </ul>
                    </ul>
                    <ul>
                        <li>
                            <strong>Organizations</strong>:
                        </li>
                        <ul>
                            <li>
                                Create your own organizations in Volta SNAP. Organization admins can invite new users to
                                join the organization
                            </li>
                            <li>
                                Shared folders for organizations have been updated that support dragging and dropping
                                models, automatically syncing changes to the shared folder for all members
                            </li>
                            <li>
                                Code libraries can now be shared with organizations. Admins can let members use the
                                organization code library in any model, or limit those codes to models in the shared
                                organization folder.
                            </li>
                            <li>The organization logo is now included on the upgrades PDF export.</li>
                            <li>
                                An admin can delete an organization. Shared models return to their owners, and members
                                are removed from the organization.
                            </li>
                            <li>
                                Users can be a member of multiple organizations, and can switch between them in the
                                Organizations page.
                            </li>
                        </ul>
                    </ul>
                    <ul>
                        <li>
                            <strong>Community Analysis</strong>:
                        </li>
                        <ul>
                            <li>Run multi-file analyses with the Community Analysis feature.</li>
                            <li>
                                Specify the models that represent the homes in the community and the number of
                                duplicates at each cardinal direction. A community can represent a housing portfolio,
                                new development, neighbourhood, or any other grouping of dwellings.
                            </li>
                            <li>
                                Define upgrade packages for the community, and unlock runs with credits, similar to the
                                Parametric Analysis feature.
                            </li>
                            <li>
                                View the impact of modifying the homes in the community with the upgrade packages
                                applied. Includes a waterfall chart that highlights the benefits of individual upgrades
                                within each package, along with other summary metrics.
                            </li>
                            <li>Export results and summary reports for the community analysis for your records.</li>
                        </ul>
                    </ul>
                    <ul>
                        <li>Other bug fixes and improvements:</li>
                        <ul>
                            <li>Fixed signing in when a sign-up was interrupted before the account was finished.</li>
                            <li>Improved pasting rows into the window table.</li>
                            <li>Fixed issues that prevented some user defined codes from saving properly.</li>
                        </ul>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 1.0.0</h3>
                    <h4>July 2026</h4>
                    <p>
                        <strong>
                            <i>Volta SNAP is out of beta!</i>
                        </strong>
                    </p>
                    <p>
                        This update introduces <strong>Insights</strong>, a redesigned room-by-room workflow, embodied
                        carbon calculations, a new credits system for paid features, and more.
                    </p>
                    <Accordion
                        bodyClassName={classes.patchAccordion}
                        heading="Patch Notes: Last Updated October 8th, 2026 - v1.0.6"
                    >
                        <h4>October 8th, 2026 - v1.0.6</h4>
                        <ul>
                            <li>
                                Added winter peak load, summer peak load, and load factor metrics to the Upgrades table.
                            </li>
                            <li>
                                Upgrade package drop-downs in Review and Results now follow the package order shown in
                                the Upgrades table.
                            </li>
                            <li>
                                Improved Enclosure component and direction filtering so newly added components and
                                components with edited directions remain visible.
                            </li>
                            <li>Other general bug fixes.</li>
                        </ul>
                        <h4>September 24th, 2026 - v1.0.5</h4>
                        <ul>
                            <li>
                                Combined Enclosure component and direction filters into a single filter panel, with a
                                sticky status bar while scrolling.
                            </li>
                            <li>Improved facing-direction filter behaviour for components.</li>
                            <li>
                                Code changes across multiple components now apply in asynchronous batches for better
                                performance on large models.
                            </li>
                            <li>Reordered window code options to match HOT2000.</li>
                            <li>Fixed room-by-room status bar layout bugs.</li>
                            <li>Other general bug fixes.</li>
                        </ul>
                        <h4>September 17th, 2026 - v1.0.4</h4>
                        <ul>
                            <li>
                                Added video tutorial links to key model tabs (Drawing, Enclosure, Upgrades, Review, and
                                Parametric Analysis), linking to the matching section on the Quick Start page.
                            </li>
                            <li>
                                Added a facing-direction filter in the Enclosure tab, so you can filter walls, windows,
                                doors, and floor headers by orientation. Includes a Clear All option and clearer filter
                                status in the room-by-room status bar.
                            </li>
                            <li>
                                Base case labels in the Upgrades table can now be reset to their default with a reset
                                button when a custom label has been entered.
                            </li>
                            <li>
                                When DHW standby loss inputs are used, the app now displays an estimated equivalent
                                energy factor for comparison with EF-rated tanks, including in upgrade labels.
                            </li>
                            <li>
                                New ventilation systems now receive default flow rates aligned with HOT2000 assumptions
                                for houses and MURBs, scaled to the number of dwelling units where applicable.
                            </li>
                            <li>
                                Improved the EnergyPlus HPXML audit report, including clearer separation of wall types,
                                correct gross vs. net area handling for windows in doors, and an expandable audit drawer
                                for long R-value summaries.
                            </li>
                            <li>Updated window metric tooltips for clarity.</li>
                            <li>Fixed vertical table heading alignment on the Net Zero Roadmap PDF export.</li>
                            <li>"Tab Details" text replaced with info button next to the tab title.</li>
                            <li>Other general bug fixes.</li>
                        </ul>
                        <h4>August 28th, 2026 - v1.0.3</h4>
                        <ul>
                            <li>
                                Added video tutorials to the Quick Start guide, covering the Drawing Capture Tool,
                                Takeoff Inputs, Upgrades, Simulations & Results, and Parametric Analysis.
                            </li>
                            <li>
                                Room-by-room results in Insights now show a detailed per-room breakdown when expanded,
                                including duct losses/gains, pipe losses, air change, and internal gains. Older results
                                prompt you to re-run a simulation to generate the full breakdown.
                            </li>
                            <li>
                                BC Step Code results now display an adjusted TEDI step and threshold when using adjusted
                                values to calculate Step compliance.
                            </li>
                            <li>Redesigned component filtering in the Enclosure tab.</li>
                            <li>
                                Restored the ability to add ceiling components to foundation rooms, and fixed walkout
                                component labels that duplicated "Walkout" in the name.
                            </li>
                            <li>Fixed display issues in the parametric pinned results summary table.</li>
                            <li>
                                Fixed disappearing or invisible door and window upgrades in the model Upgrades table,
                                along with several upgrade library fixes.
                            </li>
                            <li>
                                Code library improvements: warn when closing the code drawer without saving, prevent
                                undefined values from being saved, and fix crashes when editing searched or invalid
                                codes.
                            </li>
                            <li>Added a link to the Transactions page from the Account page.</li>
                            <li>Other general bug fixes.</li>
                        </ul>
                        <h4>August 14th, 2026 - v1.0.2</h4>
                        <ul>
                            <li>
                                Rewrote the F280 PDF export. Reports now generate as a true US Letter PDF with
                                selectable text, a smaller file size, and more reliable export (the intermittent
                                blank/black page issue is resolved).
                            </li>
                            <li>
                                Added a display units toggle in the model header to switch between Metric, Imperial, and
                                US without opening the General tab.
                            </li>
                            <li>
                                Room-by-room status checks now correctly flag rooms with a floor area of zero, and flag
                                walls or floor headers with a perimeter of zero. Room-by-room error summaries are also
                                clearer in Insights and in the Review tab.
                            </li>
                            <li>
                                Changing a DHW tank type now updates tank volume correctly, so switching away from a
                                tankless system no longer leaves a zero-volume tank.
                            </li>
                            <li>
                                Occupancy defaults now update when you change building type (house vs. MURB), including
                                occupancy scaled to the number of dwelling units.
                            </li>
                            <li>
                                Enclosure no longer switches to Room View just because rooms exist on an imported file.
                                Room View is applied automatically only when components have actually been assigned to
                                rooms.
                            </li>
                            <li>EnergyPlus hourly simulations now use OS-HPXML v1.12.0 and OpenStudio 3.11.0.</li>
                            <li>
                                Further improvements to onboarding tours, including enclosure steps that follow the
                                current view.
                            </li>
                            <li>
                                Fixed an embodied carbon summary error on basement pony walls missing an insulation
                                type.
                            </li>
                            <li>Other general bug fixes.</li>
                        </ul>
                        <h4>August 6th, 2026 - v1.0.1</h4>
                        <ul>
                            <li>Fixed logic around determination of user verification status for older accounts.</li>
                            <li>
                                Fixed a bug preventing the ability to include EV loads in the hourly analysis display.
                            </li>
                            <li>Minor updates and fixes to tour logic.</li>
                            <li>General text updates for clarity.</li>
                            <li>Other general bug fixes.</li>
                        </ul>
                    </Accordion>
                    <div style={{ height: "1rem" }}></div>

                    <ul>
                        <li>
                            Introducing <strong>Insights</strong>, a dedicated section in your models that contains a
                            variety of detailed analytical tools.
                        </li>
                        <ul>
                            <li>
                                With this release, Insights includes:{" "}
                                <strong>F280 Room-by-room analysis, EnergyPlus, and Utility Analysis</strong>.
                                Additional capabilities will be added in future updates.
                            </li>
                            <li>
                                <strong>Access and activation:</strong> Insights is activated per model for{" "}
                                <strong>1 credit</strong>, which permanently unlocks all Insights features on that model
                                and includes <strong>2,500 parametric runs</strong> when you create your first
                                parametric analysis from that model after Insights is activated.
                            </li>
                            <li>
                                On launch, Insights features are available on a free trial without activation as
                                follows:
                                <ul>
                                    <li>Room-by-room analysis until September 8th, 2026;</li>
                                    <li>EnergyPlus and Utility Analysis until April 2027.</li>
                                </ul>
                                After a feature's free trial ends, viewing results and using paid capabilities on that
                                model requires Insights to be activated.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>
                            New <strong>Credits</strong> system:
                        </li>
                        <ul>
                            <li>Credits are now available for purchase in the app header.</li>
                            <li>
                                Credits are used to access paid features within the app, which includes Insights and
                                Parametric Analysis capabilities.
                            </li>
                            <li>
                                Credits can be transferred by an organization admin to other users within the
                                organization.
                            </li>
                            <li>Added a Transactions page to view all your credit purchases, transfers, and usage.</li>
                            <li>
                                Any <strong>Legacy Credits</strong> (credits purchased prior to version 1.0.0) can be
                                transferred to the new Credits system through the Transactions page. These credits can
                                also still be used to unlock Parametric Analysis runs.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>
                            Redesigned <strong>Parametric Analysis</strong> workflow:
                        </li>
                        <ul>
                            <li>
                                Parametric Analysis runs can now be unlocked by spending credits. Each credit unlocks a
                                batch of 2,500 parametric runs.
                            </li>
                            <li>
                                Added more robust reference house calculations in the Parametric Analysis for users with
                                verified Registered Energy Advisor status.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>
                            Redesigned <strong>Room-by-room analysis</strong> workflow (Insights):
                        </li>
                        <ul>
                            <li>CSA F280 Room-by-room analysis has been moved to the Insights section.</li>
                            <li>
                                Added a new detailed room-by-room error reporting system to help you identify and fix
                                issues with your room-by-room analysis. This error reporting system is visible{" "}
                                <i>before</i> you activate Insights for a model, providing clarity around issues that
                                should be resolved before you choose to activate Insights.
                            </li>
                            <li>
                                Any model that had room-by-room results available prior to the launch of version 1.0.0
                                will have its room-by-room results and capabilities unlocked permanently.
                            </li>
                            <li>
                                If a model first had room-by-room results generated between July 31st and September 8th,
                                2026, Insights must be activated to view those results and download room-by-room reports
                                after September 8th, 2026.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>
                            <strong>EnergyPlus</strong> simulations (Insights):
                        </li>
                        <ul>
                            <li>
                                EnergyPlus simulations are now available in Insights. This is an hourly simulation
                                engine that leverages the OpenStudio-HPXML workflow.
                            </li>
                            <li>
                                At launch, these simulations perform a 1:1 simulation of the underlying HOT2000 file,
                                converted into an EnergyPlus file. Over time, additional capabilities will be added that
                                will allow users to perform analyses beyond the scope of the HOT2000 energy model (e.g.
                                larger MURBs, more electrification scenarios, operational resilience, and more).
                            </li>
                            <li>
                                EnergyPlus simulations can be run from the Insights page on one upgrade package at a
                                time, and take 10-30 seconds to process.
                            </li>
                        </ul>
                    </ul>
                    <ul>
                        <li>
                            <strong>Utility Analysis</strong> (Insights):
                        </li>
                        <ul>
                            <li>
                                Utility Data Analysis is now available in Insights. This is a feature that allows you to
                                analyze your utility data and compare it to your energy model.
                            </li>
                            <li>Use this feature to perform a demonstrated load analysis on your models.</li>
                        </ul>
                    </ul>

                    <ul>
                        <li>
                            <strong>Embodied carbon calculations</strong>:
                        </li>
                        <ul>
                            <li>
                                Embodied carbon calculations are now performed automatically on all models, requiring no
                                additional user input.
                            </li>
                            <li>
                                Updated Codes (Standard and User Defined) inputs for all assembly types that display
                                Canadian/industry-average embodied carbon values for all layers of the assembly.
                            </li>
                            <li>
                                Users can freely edit the embodied carbon values for any assembly type if more specific
                                data is available.
                            </li>
                            <li>
                                Added additional foundation component construction detail inputs to capture materials
                                (concrete, steel, etc.) commonly used in foundation construction that are not captured
                                in the energy analysis.
                            </li>
                            <li>
                                Analysis results are visible in the Review / Embodied Carbon tab within a model and
                                include: Upgrade package comparisons, building form factor impact analysis, breakdowns
                                by component and assembly type, and carbon cash flow analysis.
                            </li>

                            <li>
                                <strong>Important:</strong> The Volta SNAP embodied carbon calculation is primarily
                                intended for two purposes:
                                <ul>
                                    <li>
                                        Comparison: view the relative difference between retrofit and design decisions
                                        for components of your building included in the energy model.
                                    </li>
                                    <li>
                                        Education: learn about the embodied carbon of your building and how to reduce
                                        it. By integrating average embodied carbon values directly into the Code
                                        building workflow, we hope to begin to normalize the conversation around
                                        embodied carbon throughout the industry.
                                    </li>
                                </ul>
                                It is not intended to be a comprehensive embodied carbon calculation and should not be
                                used as a substitute for a professional embodied carbon calculation.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>Version History:</li>
                        <ul>
                            <li>
                                Revert to previous snapshots of your models, accessible from the model header menu.
                                Snapshots are saved automatically every time you run a simulation.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>New user sign-up flow:</li>
                        <ul>
                            <li>
                                New users can now freely sign up for an account and start using the app immediately as
                                an "unverified user".
                            </li>
                            <li>
                                Unverified users can create up to five (5) models (including duplicates), and cannot
                                access certain features including Insights, Parametric Analysis, non-General mode
                                simulations, and F280 reports.
                            </li>
                            <li>
                                Verification is done manually by Volta Research staff, to ensure that users are
                                professionals with sufficient knowledge and experience to use the app. Example
                                professionals include: Architects, Engineers, Energy Advisors, HVAC Designers, and other
                                similar industry professionals.
                            </li>
                            <li>
                                The ability to run simulations in EnerGuide and Ontario Reference House modes requires a
                                separate verification, and is only available for users who are Registered Energy
                                Advisors in good standing with NRCan.
                            </li>
                        </ul>
                    </ul>

                    <ul>
                        <li>New training material:</li>
                        <ul>
                            <li>
                                Added a number of new in-app training materials to help users get started with the app
                                and understand the features and capabilities.
                            </li>
                            <li>
                                Features already viewed and dismissed can be reactivated from users' account settings.
                            </li>
                            <li>Added a new set of training videos for more complex walkthroughs.</li>
                        </ul>
                    </ul>

                    <ul>
                        <li>Numerous other features, improvements, and bug fixes:</li>
                        <ul>
                            <li>More robust error handling and flagging.</li>
                            <li>
                                Updates to the Upgrades Table to improve usability. This includes showing reduced
                                operating conditions in the Upgrades Table when the user is simulating in ERS/ORH modes.
                            </li>
                            <li>
                                Updated transportation / EV modelling with newer vehicle options and clearer charge
                                preferences.
                            </li>
                        </ul>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.9.9</h3>
                    <h4>March 2026</h4>
                    <p>
                        <strong>
                            <i>The last beta!</i>
                        </strong>
                    </p>
                    <p>
                        This update features a number of quality of life improvements and bug fixes, along with a number
                        of other behind the scenes changes made ahead of the launch of the full version of the app (when
                        the version numbers mean something).
                    </p>
                    <p>
                        We have updated our{" "}
                        <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/resources/privacy">
                            privacy policy
                        </a>
                        ,{" "}
                        <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/resources/terms">
                            terms and conditions
                        </a>
                        , and{" "}
                        <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/resources/licence">
                            software licensing agreement
                        </a>{" "}
                        to reflect the new features and changes to the app.
                    </p>
                    <p>
                        It also includes a number of trial features for testing purposes that have been shared with a
                        limited number of users.
                    </p>
                    <ul>
                        <li>Quality of life improvements:</li>
                        <ul>
                            <li>
                                The main dashboard is now sortable and multiple models can be selected at once and
                                dragged to folders
                            </li>
                            <li>Base case upgrade table labels can now be edited.</li>
                            <li>
                                Added the ability to save your "favourite" groups of hidden rows in the upgrade table,
                                and apply them to other models in one click.
                            </li>
                            <li>Allow the "Info" inputs to change in size (click and drag) to fit your needs.</li>
                            <li>Addressed rounding errors in the DCF export.</li>
                        </ul>
                        <li>New Error Handling:</li>
                        <ul>
                            <li>
                                Updated the error handling system to categorize errors into critical and warning
                                categories, and display them in a new error list pop-up that focuses on critical errors.
                            </li>
                            <li>
                                Fully aligned all front-end error handling with the back-end error handling system, to
                                help make sure issues aren't buried within the interface.
                            </li>
                            <li>
                                Updated our ELA calculation to better align with HOT2000's assumptions and added a
                                warning to the ELA field when the user defined value is outside of the allowable range.
                            </li>
                            <li>
                                Now performing a more robust error check and file cleaning process to ensure that users
                                don't get locked out of the ability to save files.
                            </li>
                            <li>
                                Now performing a more robust model "codes" validation when simulating and exporting to
                                prevent "invisible" errors that could result in invalid HOT2000 files.
                            </li>
                        </ul>
                        {/* <li>Green Button Data Uploads:</li>
                        <ul>
                            <li>
                                Added the ability to upload, view, and analyze Green Button data files in the Review /
                                Utility Data tab.
                            </li>
                            <li>
                                Added the ability to search historical weather data to align with Green Button data and
                                perform a temperature-domain analysis.
                            </li>
                            <li>Added the ability to export Green Button data to a CSV file.</li>
                        </ul> */}
                        <li>Other alpha test features that will be available in the full version of the app.</li>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.7</h3>
                    <h4>August 2025</h4>
                    <p>Room-by-room, parametric analysis, automated error reporting and more!</p>
                    <Accordion
                        bodyClassName={classes.patchAccordion}
                        heading="Patch Notes: Last Updated March 13th, 2026"
                    >
                        <h4>March 13th, 2026</h4>
                        <ul>
                            <li>
                                Added a warning to the F280 PDF export when the user is not verified as a qualified CSA
                                F280-12 designer, in an aim to push those who haven't completed the training to do so.
                            </li>
                            <li>Added a warning to the F280 PDF export when the PDF is not formatting properly.</li>
                            <li>
                                Fixed bugs that could cause potential app crashes when saving files if empty code data
                                was present.
                            </li>
                            <li>Increased robustness of the saving function to prevent potential data loss.</li>
                            <li>
                                Fixed a rounding error bug that could cause a warning that internal gains were not fully
                                assigned.
                            </li>
                        </ul>
                        <h4>March 12th, 2026</h4>
                        <ul>
                            <li>
                                Updated the method of calculating ELA to use the same flow exponent assumptions as
                                HOT2000.
                            </li>
                            <li>
                                Added a warning to the ELA field when the user defined value is outside of the allowable
                                range.
                            </li>
                            <li>Changed link behaviour to open the Updates page in a new tab.</li>
                            <li>Fixed typos in some of the basement configuration option descriptions.</li>
                            <li>Numerous updates for the closed Community Analysis beta test feature.</li>
                        </ul>
                        <h4>March 3rd, 2026</h4>
                        <ul>
                            <li>
                                Fixed gross heat loss performance thresholds for the NBC 2020/2025 tiered energy code
                                display in the parametric analysis feature.
                            </li>
                            <li>Numerous updates for the closed Takeoff Sheet Upload beta test feature.</li>
                        </ul>
                        <h4>February 9th, 2026</h4>
                        <ul>
                            <li>
                                Fixed a bug with the room splitting feature that could produce walls and floor headers
                                with 0 perimeter values.
                            </li>
                            <li>
                                Allowing manual entry for room areas and heights, only syncing these values with
                                component dimensions if the user has not altered the field values.
                            </li>
                            <li>Minor bug fixes to prevent potential app crashes</li>
                        </ul>
                        <h4>December 18th, 2025</h4>
                        <ul>
                            <li>
                                Adjustments to the CHBA Net Zero Workbook generator to allow for different export
                                options depending on whether labelling a new build or a renovation.
                            </li>
                            <li>
                                Added a warning to the CHBA Net Zero Workbook generator when the file does not contain
                                the CHBA Net Zero Building Envelope package.
                            </li>
                            <li>
                                Made a number of adjustments to the CHBA Towards Net Zero Renovation case study
                                generator to allow for varying levels of configurability.
                            </li>
                            <li>Fixed styling around sticky upgrade table headers.</li>
                            <li>Numerous minor bug fixes and performance improvements.</li>
                        </ul>
                        <h4>December 9th, 2025</h4>
                        <ul>
                            <li>
                                Updated the method of building CHBA Net Zero case studies to allow for more flexibility
                                in the input data.
                            </li>
                            <li>
                                Added the ability to link an "E-file" to the CHBA Net Zero case study to pull in results
                                from a different model.
                            </li>
                            <li>Updated the HOT2000 file upload field to allow for drag-and-drop uploads.</li>
                            <li>
                                Added a number of simplifications to the method of defining inputs for a parametric
                                analysis and mechanical systems.
                            </li>
                            <li>
                                Changed the upgrades table so that its header now sticks to the top of the page when
                                scrolling.
                            </li>
                        </ul>
                        <h4>November 24th, 2025</h4>
                        <ul>
                            <li>Updated user session authentication to log users out after 2 weeks.</li>
                            <li>
                                Fixed a bug that wouldn't allow negative values to be entered into the interpolated F280
                                heat loss field.
                            </li>
                        </ul>
                        <h4>November 12th, 2025</h4>
                        <ul>
                            <li>
                                Added the error list pop-up to the upgrades table when simulations run without returning
                                results.
                            </li>
                            <li>
                                Added a warning about ventilation requirements when the total specified flow rate does
                                not meet the minimum requirements.
                            </li>
                            <li>
                                Added the ability to export the three required CHBA NZ files for submission purposes
                                (building envelope file, proposed design file, and proposed design file with PV).
                            </li>
                            <li>
                                Added the ability to save and apply CHBA NZ verification and IAQ checklists for the
                                Registration workbook from your organization's templates.
                            </li>
                        </ul>
                        <h4>October 30th, 2025</h4>
                        <ul>
                            <li>Added BC Energy Step Code compliance metrics to the upgrades table.</li>
                            <li>
                                Added the ability to view F280 whole-home results at an intermediary (interpolated or
                                extrapolated) outdoor temperature.
                            </li>
                            <li>Addded missing NBC 2020 performance tier metrics to folder export functionality.</li>
                        </ul>
                        <h4>October 28th, 2025</h4>
                        <ul>
                            <li>
                                Fixed a bug that could crash the CHBA NZ case study generator when results weren't
                                present in the expected format.
                            </li>
                            <li>Fixed a number of minor bugs that could crash the drawing tool.</li>
                        </ul>
                        <h4>October 23rd, 2025</h4>
                        <ul>
                            <li>
                                Fixed a bug that could result in the inability to save files if actions were cancelled
                                while fields were in a temporarily invalid state.
                            </li>
                        </ul>
                        <h4>October 8th, 2025</h4>
                        <ul>
                            <li>
                                Added a warning to the upgraded airtightness drawer to notify users when pre-defined
                                drop-down air leakage values should not be used (when air leakage test data is used in
                                the base case).
                            </li>
                            <li>Added a warning to flag missing code data from within the upgrades drawer.</li>
                            <li>
                                Added a warning to flag missing code data from an upgrade when viewed from the upgrade
                                library management screen.
                            </li>
                            <li>
                                Fixed logic around determining Flat/Cathedral ceilings in the upgrades table, upgrades
                                library, and parametric analysis.
                            </li>
                            <li>Removed error flags for foundation upgrade cost/priority fields.</li>
                            <li>
                                Fixed a bug that could sometimes not properly account for interior added insulation in
                                basement upgrades, depending on a specific combination of base file and upgrade
                                parameters.
                            </li>
                        </ul>
                        <h4>September 29th, 2025</h4>
                        <ul>
                            <li>
                                Fixed performance issues for parametric analysis data loading and graph rendering for
                                large datasets.
                            </li>
                            <li>
                                Fixed bug with the parametric analysis that could mistakenly force a "No Change" option
                                to be excluded from runs if it wasn't included in the initial run.
                            </li>
                        </ul>
                        <h4>September 19th, 2025</h4>
                        <ul>
                            <li>
                                Updated method of notifying users that the app version has changed. Now with the ability
                                to proceed with work before choosing to reload.
                            </li>
                            <li>
                                Fixed a bug that could cause unexpected behaviour when deleting or splitting components
                                that had upgrades applied.
                            </li>
                            <li>Fixed a bug impacting window table copy + paste imports.</li>
                            <li>Added a timeout to parametric simulation runs.</li>
                            <li>Fixed various minor bugs in the drawing tool.</li>
                            <li>
                                Fixed a bug that could prevent users from defining a specific ACH amount in an upgrade
                                case when the base case used air leakage test data inputs.
                            </li>
                            <li>Fixed a bug impacting saving the CHBA NZ Renovation roadmap when uploading files.</li>
                            <li>
                                Fixed a bug that was preventing the cover image from getting included in the CHBA NZ
                                Renovation case study export.
                            </li>
                        </ul>
                    </Accordion>
                    <div style={{ marginTop: "2rem" }}></div>
                    <ul>
                        <li>
                            Added the ability to perform verified <strong>room-by-room CSA F280-12 calculations</strong>
                            .
                            <ul>
                                <li>
                                    Updates to the enclosure tab to enable room-by-room calculations, including the
                                    ability to sort components by storey (normal) or room view, filter components, and
                                    track progress towards a completed set of valid room-by-room inputs.
                                </li>
                                <li>
                                    Split components by breaking up their perimeters, areas, and subcomponents and
                                    assigning them to rooms, allowing the use of a "typical" ERS file (composed of a
                                    small number of large components) to act as a starting point for a room-by-room
                                    analysis.
                                </li>
                                <li>
                                    Added the ability to upload a HOT2000 file with "Room" components (outside of ERS
                                    scope).
                                </li>
                                <li>Added room-by-room page to F280 PDF export if calculation is enabled.</li>
                            </ul>
                        </li>

                        <li>
                            <strong>Parametric analysis (500 run free trial).</strong>
                            <ul>
                                <li>
                                    Run thousands of Simulations on your energy models to find the cost-optimal pathways
                                    to your goals.
                                </li>
                                <li>
                                    Input capital costs for measures either at a high-level or using detailed inputs
                                    (e.g. $/ft2, labour and material costs).
                                </li>
                                <li>
                                    In addition to creating upgrade measures from scratch, import pre-built upgrade
                                    measures (
                                    <ParametricPremadeIcon
                                        style={{ position: "relative", top: "2px" }}
                                    ></ParametricPremadeIcon>
                                    ), measures from the source model (<ParametricUpgradeIcon></ParametricUpgradeIcon>),
                                    or a group of measures from a saved upgrade package.
                                </li>

                                <li>
                                    Current calculated metrics include all major HOT2000 metrics:
                                    <ul>
                                        <li>
                                            Annual emissions using the latest National Inventory Report values for each
                                            province;
                                        </li>
                                        <li>Whole-home F280 heat loss and gain;</li>
                                        <li>MEUI, TEDI, and EUI;</li>
                                        <li>Total cost of measures (capital cost);</li>
                                        <li>Operating cost using user-defined utility data; and,</li>
                                        <li>Total cost of ownership over configurable time horizon.</li>
                                    </ul>
                                </li>
                                <li>
                                    For users with Registered Energy Advisor Accounts, include building code (NBC)
                                    metrics to determine performance tier.
                                </li>

                                <li>
                                    This update includes a free trial for all users who do not already have credits of
                                    up to 500 simulations on one model.
                                </li>
                                <li>
                                    Should anyone wish to purchase credits for more analyses, prior to the launch of
                                    in-app payments later this year, they can contact info@voltaresearch.org
                                </li>
                            </ul>
                        </li>

                        <li>
                            Automated error reporting.
                            <ul>
                                <li>
                                    Volta SNAP developers are now automatically notified of app crashes and cases of
                                    simulations running without returning results.
                                </li>
                                <li>
                                    Manual error reporting mechanism moved to the megaphone icon in the bottom right
                                    corner of the screen.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Added transportation calculations.
                            <ul>
                                <li>
                                    Compare the cost and emissions associated with internal combustion, plug-in hybrid,
                                    and electric vehicle usage via the new inputs in the "Systems" tab.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Additional CHBA Net Zero capabilities.
                            <ul>
                                <li>Towards Net Zero Renovation case study generation.</li>
                                <li>Automated Net Zero Registration Workbook creation.</li>
                            </ul>
                        </li>
                        <li>HOT2000 version 11.13 verification with NRCan passed.</li>
                        <li>User-managed password recovery added.</li>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.6</h3>
                    <p>HOT2000 v11.13 compliance (Beta)</p>
                    <ul>
                        <li>Added the ability to import, export, and simulate file in HOT2000 version 11.13.</li>

                        <li>
                            Added and modified fields when editing a file in HOT2000 v11.13:
                            <ul>
                                <li>ASHP efficiency inputs updated to allow entering HSPF2/SEER2 values.</li>
                                <li>Updated options in AC/ASHP equipment type fields.</li>
                                <li>
                                    WSHP and GSHP efficiency inputs updated to allow entering COP for heating and COP or
                                    EER for cooling efficiency.
                                </li>
                                <li>Alignment with solar orientation inputs.</li>
                                <li>Added Owner Occupied field to File section</li>
                                <li>
                                    Migrated all Greener Homes inputs to Resiliency Measures sections (Programs and
                                    Energy Upgrades).
                                </li>
                            </ul>
                        </li>
                        <li>
                            Minor interface messaging updates to align with our latest applicable HOT2000 usage license.
                        </li>
                    </ul>
                    <p>
                        Version 11.13 compatibility will exit "Beta" after NRCan staff have reviewed compliance per the
                        latest license agreement.
                    </p>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.2.5</h3>
                    <p>
                        Added in-app "Organizations" with file and upgrade package sharing, detailed costing inputs and
                        more:
                    </p>
                    <ul>
                        <li>
                            Added in-app organizations and supporting capabilities.
                            <ul>
                                <li>
                                    Request an organization for yourself and your colleagues via the request button on
                                    the main dashboard.
                                </li>
                                <li>
                                    Share files with read & write permissions with colleagues within your organization.
                                </li>
                                <li>
                                    Save upgrade packages to your organization, allowing your colleagues to access a
                                    shared library of upgrade packages. Saved packages can be used for a variety of
                                    purposes. For example, a net zero package for a given climate zone, or a code
                                    package used in the upgrades table for comparison purposes.
                                </li>
                                <li>
                                    Non-file owners can choose to remove shared files from their dashboard, while the
                                    file owner must delete its data.
                                </li>
                                <li>
                                    Shared files support simultaneous editing by different users. Users are notified
                                    when changes have been made to their file since it was last loaded and are provided
                                    with the option to either merge their changes or discard their changes and resync
                                    the file with the database.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Added detailed costing and financial inputs for upgrades.
                            <ul>
                                <li>
                                    When adding any type of upgrade, use the "Upgrade Costs" section at the bottom of
                                    the drawer to enter different cost line items associated with that upgrade measure.
                                    Users have the flexibility to define costs based on a variety of line item options.
                                </li>
                                <ul>
                                    <li>
                                        Line items allow the entry of "bulk" costs (e.g. total material cost, total
                                        labour cost, overall cost), cost per unit (e.g. cost per square foot of
                                        component), tax, and rebates.
                                    </li>
                                </ul>
                                <li>
                                    When these costs are saved as a part of an upgrade package, those tied to the model
                                    (e.g. cost per square foot of attic) will update accordingly when applied to a new
                                    model.
                                </li>
                                <li>
                                    Added inputs for Grants, Loans, and Financial Valuation Parameters to the upgrades
                                    table.
                                </li>
                                <li>
                                    Added Total Cost of Ownership and NPV metrics to the results section of the upgrades
                                    table.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Added a new method of flagging model errors.
                            <ul>
                                <li>
                                    Users can now flag a model with an error for review by our developers via the Model
                                    Settings drawer: <SettingsIcon />
                                </li>
                                <li>
                                    This allows all our developers to group together and triage review requests, adding
                                    redundancy and removing potential bottlenecks.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Added additional building code (NBC) metrics to the upgrades table.
                            <ul>
                                <li>
                                    For NBC 2020 performance compliance, the upgrade table now displays the achieved
                                    performance tier as well as the metrics used to determine compliance (overall
                                    percent improvement, heat loss reduction, and peak cooling validation).
                                </li>
                                <li>
                                    Includes operational carbon metrics to be included in the 2025 NBC (PCF 2004),
                                    including the proposed operational emissions and resulting compliance level.
                                </li>
                                <li>
                                    Includes compliance tier achieved under the Energy Use Intensity compliance path to
                                    be included in the 2025 NBC (PCF 1869).
                                </li>
                            </ul>
                        </li>

                        <li>
                            Import blower door test data files (.bld) to automatically populate the air leakage test
                            data section of the file.
                            <ul>
                                <li>Added air leakage test data to the DCF export.</li>
                            </ul>
                        </li>
                        <li>
                            Allow users to input NLR targets to calculate the resulting ACH and include NLR in the
                            upgrade table label when specified.
                        </li>
                        <li>
                            Added the image gallery.
                            <ul>
                                <li>
                                    Allows users to upload photos of their home assessment and tag them based on a
                                    variety of pre-configured and custom tags
                                </li>
                                <li>
                                    Helps users track photos required by ERS procedures based on the content of their
                                    file.
                                </li>
                                <li>
                                    Supports the forthcoming integration of the mobile app, allowing photos taken on a
                                    user's mobile device to be uploaded with the rest of their assessment data.
                                </li>
                                <li>Supports case study creation for CHBA Net Zero Renovation projects.</li>
                            </ul>
                        </li>
                        <li>
                            Background work and first "community" prototype for a subset of users (CHBA Net Zero
                            Renovation projects).
                            <ul>
                                <li>Multi-model summary screen with filters</li>
                                <li>Multi-model results summary screen with filters.</li>
                            </ul>
                        </li>

                        <li>
                            Other bug fixes and improvements:
                            <ul>
                                <li>
                                    A double click is now required to close the upgrade drawer to prevent unintentional
                                    discarding of changes without saving.
                                </li>
                                <li>
                                    Updated the UI around upgrade drawer confirmation buttons so that they are always
                                    visible.
                                </li>
                                <li>Updated tab UI to make the selected tab more clear.</li>
                                <li>Added the ability to delete multiple files at once from the main dashboard.</li>
                                <li>
                                    Added lazy loading for drop-down menus to prevent performance issues around
                                    components and "Type" fields. Component accordions and type fields should no longer
                                    lag when opening for users with large code libraries.
                                </li>
                                <li>Added a 404 page.</li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.4</h3>
                    <p>
                        Refactored the Drawing Capture Tool, added the ability to type equations in fields, link the
                        upgrade table to other models, and more:
                    </p>
                    <ul>
                        <li>
                            Refactored the Drawing Capture Tool.
                            <ul>
                                <li>
                                    Completely rebuilt the drawing tool to refine functionality and increase
                                    performance. Any existing drawings will still be present in models, but shapes will
                                    need to be rebuilt (linked HOT2000 components are unaffected).
                                </li>
                                <li>
                                    Generalized available shapes and added the "Multi-point Line" tool to handle partial
                                    perimeters.
                                </li>
                                <li>
                                    Hold "s" and click when drawing shapes to snap the point to the nearest indicated
                                    line.
                                </li>
                                <li>
                                    With a shape selected, hold Shift and click near a line to add a new point to an
                                    existing shape, or hold Shift and click a point to delete it from the shape.
                                </li>
                                <li>Hold "i" to show all shape information at once on the screen.</li>
                                <li>
                                    Improved the flow around setting the scale of the drawing. Scales can now be applied
                                    to multiple pages at once.
                                </li>
                                <li>
                                    View drawing pages individually and switch between pages via their thumbnails in the
                                    left-hand navigation pane.
                                </li>
                                <li>Scroll zoom.</li>
                                <li>
                                    Measurements from the drawing tool are now decoupled from HOT2000 components.
                                    Measurements pass in one direction from the Drawing Capture Tool to Takeoff fields
                                    to HOT2000 components. Measurements acquired at one stage no longer "lock"
                                    measurements at a subsequent stage.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Added the ability to type equations directly into fields.
                            <ul>
                                <li>
                                    Added the ability to enter numeric values via equation inputs directly into fields
                                    by beginning typing with the equals (=) symbol.
                                </li>
                                <li>
                                    Supported operations include addition (+), subtraction (-), multiplication (*),
                                    division (/), and exponents (^), and the order of operations can be controlled with
                                    brackets.
                                </li>
                                <li>
                                    This capability is currently only applied to measurement fields in the enclosure
                                    tab. If you believe any fields should have this ability added, let us know.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Added the ability to link a separate model as the base case in the upgrades table ("linked
                            base case").
                            <ul>
                                <li>
                                    Select a different model to act as the base case/existing home in the upgrades table
                                    to compare results for retrofits where housing geometry changes.
                                </li>
                                <li>
                                    Pre-retrofit files should be selected as the linked base case for post-retrofit
                                    files.
                                </li>
                                <li>The selected linked base case model must contain a complete set of results.</li>
                            </ul>
                        </li>
                        <li>
                            Create HOT2000 window components by pasting tabular window data.
                            <ul>
                                <li>
                                    This feature is now available when entering data in HOT2000 mode by clicking "..."
                                    on a component header followed by Window Table in the add components section of the
                                    drop-down.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Utility bill inputs.
                            <ul>
                                <li>Allows entering of pre- and post-retrofit utility bills in the Review tab.</li>
                                <li>
                                    Supports future features aimed at estimating real-world consumption characteristics.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Added the ability to export .h2k files with upgrades applied to the base file and/or include
                            results in the exported file.
                        </li>
                        <li>
                            Added NLR and NLA metrics to the Airtightness tab, along with the calculated building
                            enclosure area used to perform these calculations.
                        </li>
                        <li>
                            CSA F280-12 report format updates to better align with industry and submission requirements.
                        </li>
                        <li>
                            CHBA-specific features:
                            <ul>
                                <li>CHBA Net Zero Renovation project roadmap.</li>
                                <li>Indoor air quality section added to Net Zero Workbook.</li>
                            </ul>
                        </li>
                        <li>
                            General bug fixes and improvements, including:
                            <ul>
                                <li>
                                    Fixed issue that would result in imperial units sometimes rounding by very small
                                    amounts.
                                </li>
                                <li>
                                    Fixed a bug that would sometimes accidentally delete code information from a model,
                                    resulting in the creation of corrupt h2k files.
                                </li>
                                <li>
                                    Added warnings to catch potential simulation issues when a basement's subcomponent
                                    area exceeds its pony wall area, if applicable.
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.3</h3>
                    <p>
                        Added the upgrade package library, house rotation, HOT2000 v11.12 support, achieved CSA F280-12
                        compliance, and more:
                    </p>
                    <ul>
                        <li>
                            Added support for HOT2000 v11.12.
                            <ul>
                                <li>
                                    Version 11.11 and 11.12 files can now be simulated using our more robust HOT2000
                                    simulation engine service, which is less prone to errors and experiences less
                                    downtime.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Save upgrades and apply them to any house file through saved upgrade packages.
                            <ul>
                                <li>
                                    Save upgrade packages from within a model by clicking "..." next to the upgrade
                                    package name.
                                </li>
                                <li>
                                    Apply saved upgrade packages to another file through the Add New Package dialog box.
                                </li>
                                <li>
                                    Manage your saved upgrades from the Upgrade Library, accessible in the left-hand
                                    navigation bar.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Added new metrics to the Upgrades Table.
                            <ul>
                                <li>
                                    For simulations in ERS mode, click the drop-down menu in the simulation row divider
                                    (next to "All Simulation Results") to view the energy, emissions, or cost-saving
                                    contribution of each individual upgrade.
                                </li>
                                <li>
                                    Added the following metrics to the simulation section: Gross Heat Loss Improvement,
                                    EUI, TEDI, MEUI.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Added the ability to rotate all of the windows on your home via the Rotate Home button in
                            the Enclosure Tab.
                        </li>

                        <li>
                            Added the ability to apply any code to a subset of HOT2000 components.
                            <ul>
                                <li>
                                    When creating or editing a code via the code drawer, use the new multi-select
                                    drop-down menu to apply the code to multiple components.
                                </li>
                            </ul>
                        </li>

                        <li>
                            Minor adjustments to the CSA F280-12 heat loss and heat gain calculation to achieve
                            whole-home calculation verification.
                        </li>

                        <li>
                            Added CHBA Net Zero project-specific features.
                            <ul>
                                <li>File tagging and sharing with CHBA Project Administrators.</li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.2</h3>
                    <p>
                        Added a data collection form (DCF) export, F280 summary page and report export, tabular window
                        inputs, and collapsable side bar:
                    </p>
                    <ul>
                        <li>Data collection forms (.xlsx) can now be exported in the Review / Export to H2k tab.</li>
                        <ul>
                            <li>Blower door test data not yet supported.</li>
                        </ul>

                        <li>
                            View a dedicated summary page for CSA F280-12 calculations in the Review tab.
                            <ul>
                                <li>Toggle the upgrade package to include in the summary view.</li>
                                <li>
                                    View heat loss / gain by building component, and see the calculated effective
                                    R-value used in the calculation.
                                </li>
                                <li>Download a PDF summary report.</li>
                            </ul>
                        </li>

                        <li>
                            Set the data entry mode to Takeoff or Combined and copy and paste tabular window data (TSV)
                            from excel to automatically extract all window measurements.
                        </li>
                        <ul>
                            <li>
                                Each window should be represented by a row and column headers and units should match the
                                specifications in the window table input drawer.
                            </li>
                            <li>Choose whether to overwrite or append to windows that may already be specified.</li>
                            <li>
                                Click "Convert Takeoff" to apply windows to the main wall associated with a given
                                storey.
                            </li>
                        </ul>
                        <li>Collapse the side navigation bar to utilize more space on smaller screens.</li>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.1</h3>
                    <p>Dashboard folders, duplicate models, and CHBA Net Zero Renovation project-specific support:</p>
                    <ul>
                        <li>Added folders to the house model dashboard:</li>
                        <ul>
                            <li>Create, name, move, and delete folders.</li>
                            <li>Move models into folders, and duplicate models.</li>
                            <li>
                                When deleting a folder, all of its contents will be moved to the root level model
                                directory.
                            </li>
                        </ul>

                        <li>
                            Added the CHBA Net Zero Project Registration Workbook to the review tab (only accessible to
                            CHBA Net Zero qualified EAs).
                        </li>

                        <li>Other improvements include:</li>
                        <ul>
                            <li>
                                Modified the CSV download for hourly calculations to separate electricity consumption by
                                end-use (heating, cooling, hot water, ventilation, base loads, generation).
                            </li>
                            <li>
                                Fixed a bug in the hourly calculation procedure that could inadvertently lead to
                                overestimating hourly electrical heat pump loads.
                            </li>
                            <li>
                                Fixed a bug that caused the total energy consumption display to not properly show the
                                net annual energy consumption for net zero cases that are not all-electric.
                            </li>
                            <li>
                                Added PV production (GJ and kWh) rows to the Upgrades table and displaying gross
                                electricity consumption in the Fuel Consumption and Production section of the Upgrades
                                table for greater transparency.
                            </li>
                            <li>
                                Added the ability to override indoor set points for F280-12 design heat loss and gain
                                calculations. Note that using temperature set point overrides will produce results that
                                are not compliant with CSA F280-12. This feature should only be used for exploratory
                                purposes at the discretion of an experienced energy advisor.
                            </li>
                        </ul>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.2.0</h3>
                    <p>New geometry inputs, model defaults, and CSA F280-12 whole-home calculations:</p>
                    <ul>
                        <li>Added new geometry "Takeoff" inputs:</li>
                        <ul>
                            <li>
                                Enter geometry information in a simpler manner than directly into HOT2000 components.
                            </li>
                            <li>
                                Switch between three view/data entry modes:
                                <ul>
                                    <li>HOT2000: View only HOT2000 components</li>
                                    <li>Takeoff: View only Takeoff fields and components</li>
                                    <li>Combined: View both HOT2000 and Takeoff components together</li>
                                </ul>
                            </li>
                            <li>
                                Automatically generate HOT2000 components from Takeoff inputs by clicking the "Convert
                                Takeoff" button, and automatically calculate heated floor areas, house volume, and the
                                height of highest ceiling.
                            </li>
                            <li>
                                Add "Takeoff components" to account for complex geometries and associated surface areas
                                and volumes, such as cathedral and sloped ceilings or buffered walls.{" "}
                            </li>
                            <li>
                                Add windows via simplified table inputs. When naming your window, use the format
                                "windowname_SE" to define the facing direction as South East.
                            </li>
                        </ul>

                        <li>
                            Added "Model Default" inputs, accessible via the Model Settings (gear) icon:{" "}
                            <SettingsIcon />
                        </li>
                        <ul>
                            <li>
                                Define default component construction types for any HOT2000 component type. When
                                creating new components (either by creating a HOT2000 component from scratch, or
                                clicking "Convert Takeoff"), they will be pre-populated with the default construction
                                type.
                            </li>
                        </ul>

                        <li>Added CSA F280-12 heat loss and heat gain calculations (BETA):</li>
                        <ul>
                            <li>
                                Through the Upgrades table interface, view the CSA F280-12-compliant design heat loss
                                and heat gain for a home as built or designed, and compare changes to peak loads across
                                different upgrade packages.
                            </li>
                            <li>
                                The calculation procedure is supported by an effective R-value calculator in the
                                back-end that calculates component effective R-value compliant with both CSA F280-12 and
                                the 2020 National Building Code.
                            </li>
                            <li>
                                At the time of release (April 2023), the official software validation procedure was not
                                yet published. However, the calculation performed in Volta SNAP has been validated
                                against the preliminary test cases and was shown to be compliant.
                            </li>
                        </ul>

                        <li>Other improvements include:</li>
                        <ul>
                            <li>Export the Upgrades table in PDF format (ledger or two 8.5" x 11" pages).</li>
                            <li>The ability to change passwords in-app.</li>
                            <li>General bug fixes and improvements.</li>
                        </ul>
                    </ul>
                </div>

                <div className={classes.row}>
                    <h3>Update 0.1.9</h3>
                    <p>Added upgrade package comparison table and hourly load calculations. Major changes include:</p>
                    <ul>
                        <li>Added upgrade package table:</li>
                        <ul>
                            <li>
                                Define upgrades for up to 10 "packages"/"pathways" in one place and quickly compare them
                                through the new table view.
                            </li>
                            <li>
                                Enter your own upgrade label or use an automatically generated label to identify
                                upgrades.
                            </li>
                            <li>Simulate directly in the upgrades table to directly compare results.</li>
                            <li>
                                Optionally include costs for each upgrade line item and view operational costs and
                                simple payback by filling in local fuel costs in the General tab.
                            </li>
                            <li>
                                Hover over a row label and click the eye icon to select rows to hide, and click the
                                "Hide Rows"/"Show Rows" button at the top of the table to toggle hidden rows on and off.
                            </li>
                        </ul>
                        <li>Perform hourly electrical load calculations:</li>
                        <ul>
                            <li>
                                Estimate hourly electrical loads for simulations run on homes in British Columbia,
                                Alberta, Ontario, and Nova Scotia (remaining Provinces and Territories coming soon).
                            </li>
                            <li>
                                Added the ability to specify performance curve data (COP vs ambient temperature) for air
                                source heat pumps to produce more accurate hourly electrical load estimates.
                            </li>
                            <li>
                                Calculate electrical utility costs using time-of-use (TOU) and/or tiered pricing where
                                applicable. Compare the results of both for jurisdictions that allow the choice between
                                TOU and tiered pricing.
                            </li>
                            <li>
                                Estimate hourly emissions from electricity consumption in Ontario using{" "}
                                <a rel="noopener noreferrer" href="https://www.ieso.ca/power-data" target="_blank">
                                    IESO grid generation data
                                </a>
                                . Select between emission factors (life cycle or fossil fuel-only) for the last 10
                                years.
                            </li>
                            <li>
                                Download a CSV file of hourly loads of emissions (where applicable) to perform your own
                                analysis offline.
                            </li>
                        </ul>

                        <li>Added user preferences:</li>
                        <ul>
                            <li>Click your name in the upper left to access user preferences.</li>
                            <li>Enter values to be automatically applied to every new model you create.</li>
                        </ul>

                        <li>General quality of life improvements:</li>
                        <ul>
                            <li>
                                Tab between fields (including drop-down menus) and use arrow keys/enter to select
                                values. Note that arrow keys cannot be used in drop-down menus with search
                                functionality, but these fields can still be accessed via tab.
                            </li>
                            <li>
                                Added default component names, and allow users to press enter to close dialogue boxes
                                (for example, when naming components).
                            </li>
                            <li>
                                Changed "edit code" behaviour to mirror HOT2000. Editing a code library (favourite) code
                                will update all instances of that code in the active model. Editing a non-code library
                                code will now only modify the code in the component targeted.
                            </li>
                            <li>
                                Added building code selection in the General tab to facilitate future improvements and
                                features that tie into building code requirements.
                            </li>
                            <li>
                                Added minimum ventilation rate field to the Enclosure tab. The value is based on the
                                number of bedrooms and the selected building code.
                            </li>
                            <li>Fixed a bug where some fields wouldn't show errors when invalid.</li>
                        </ul>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.8</h3>
                    <p>Added temporary solution to print all inputs:</p>
                    <ul>
                        <li>
                            Modified page styles to remove the side and top bar when printing webpage (crtl+P or ⌘+P).
                        </li>
                        <li>
                            Added open/close button to expand or condense all components within tabs to print content
                            within each tab. Stacks subtabs (Mechanical tab) and expands all accordion components
                            (General, Enclosure, Airtightness tabs).
                            <ul>
                                <li>
                                    Note that due to the amount of content that must be rendered at once, browsers will
                                    likely experience performance issues when expanding/opening all components,
                                    particularly in the Enclosure tab. If the page appears frozen, the browser is
                                    working on rendering all of the content, and the open/close button should not be
                                    repeatedly toggled.
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.7</h3>
                    <p>Added simulations and upgrades. Major changes include:</p>
                    <ul>
                        <li>
                            In-app HOT2000 simulations:
                            <ul>
                                <li>
                                    Simulations can be run in General, ERS, and ORH modes. For ERS and ORH modes, users
                                    must confirm that they are certified energy advisors who are permitted to run the
                                    energy advisor version of HOT2000.
                                </li>
                                <li>
                                    Multiple upgrade packages can be simulated at once (max 10), and results between
                                    packages and operating conditions can be toggled via drop-down selections.
                                </li>
                                <li>
                                    Please note that the stability and response time of the h2k-cloud simulation engine
                                    is continuously being improved. We will continue to roll out enhanced error
                                    messaging around in-app simulations.
                                </li>
                                <li>When exporting a file, results are not appended (in development).</li>
                                <li>Simulations can only be run for version 11.11 files and onward.</li>
                            </ul>
                        </li>
                        <li>
                            Upgrades and upgrade packages:
                            <ul>
                                <li>
                                    Upgrade packages allow users to model multiple sets of upgrades that are not
                                    possible within a single .h2k file, allowing users to analyze performance associated
                                    with various changes to the same component or system type. This will enable upgrades
                                    to be investigated without making multiple copies of an h2k file.
                                </li>
                                <li>Files can be exported with a single upgrade package included.</li>
                            </ul>
                        </li>
                        <li>
                            Tab navigation changes:
                            <ul>
                                <li>Replaced the Additional tab with the Upgrades tab.</li>
                                <li>Moved the Temperature and Base Loads sub-tabs to the Mechanicals tab. </li>
                                <li>Moved the Program and Information sub-tabs to the Review Tab.</li>
                            </ul>
                        </li>
                        <li>Added a variety of QA warnings throughout both the front- and back-end of the app.</li>
                        <li>
                            Added the basement composite R-value calculator drawer, accessed through the pencil icon
                            when foundation wall fields are set to User Specified.
                        </li>
                        <li>
                            Added a drawer to select foundation insulation configuration options for all foundation
                            types.
                        </li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.6</h3>
                    <p>General bug fixes and improvements, including:</p>
                    <ul>
                        <li>Implemented login session timeout.</li>
                        <li>
                            Fixed decimal inconsistency with AC efficiency field, updated heat pump water heater default
                            EFs to comply with workaround.
                        </li>
                        <li>Modified underlying drawing functions to improve app speed.</li>
                        <li>Thicker line hitbox for selecting and dragging lines and scales.</li>
                        <li>
                            Added "active" and "inactive" shapes, where active shapes are indicated by the presence of
                            anchor points.
                        </li>
                        <li>
                            Shapes must now be activated (single click) before they can be dragged or resized to prevent
                            unintentionally altering shapes when dragging throughout the canvas.
                        </li>
                        <li>
                            Added initial "snapping" functionality when holding the Shift key:
                            <ul>
                                <li>
                                    Holding shift while dragging a polygon anchor will align it vertically or
                                    horizontally to the nearest point(s), which can be used to achieve right angles in
                                    polygons.
                                </li>
                                <li>
                                    Holding shift while dragging a line or scale anchor point will snap the line to the
                                    nearest 15˚ increment (0˚, 15˚, 30˚, 45˚, 60˚, 75˚, 90˚) in all four circle
                                    quadrants.
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.5</h3>
                    <p>
                        Added User Defined codes, the Code Library, and refactored overall data structure. Major changes
                        include:
                    </p>
                    <ul>
                        <li>
                            Added the Code Library. Users can now upload and manage their building assembly codes. When
                            importing a new or more recent code library, older codes within Volta SNAP will be
                            overwritten with newer incoming codes based on matching code labels.
                        </li>
                        <li>
                            Added User Defined codes. Users can now create more detailed codes directly in a model or
                            from the code library.
                        </li>
                        <li>
                            Refactored the way "type" fields (e.g. wall type, window type) are stored in the data
                            structure. This will make the way that data is passed to and from HOT2000 more robust and
                            minimize display issues causing "type" fields to sometimes appear empty after importing a
                            file.
                        </li>
                        <li>Clarified language around nominal and effective R-Values.</li>
                        <li>
                            Added "Number of Identical Windows" fields to all windows, including in the drawing. Note
                            that changing this number does not create another drawing element.
                        </li>
                        <li>Updated window to wall ratio calculation to properly account for duplicate windows.</li>
                        <li>
                            Fixed a bug that sometimes caused importing files with certain types of upgrades to fail.
                        </li>
                        <li>
                            Fixed an issue that prevented User Specified R-values from displaying in the code compliance
                            table.
                        </li>
                        <li>
                            Fixed bugs associated with the "Depressurization Limit" and "Depressurization Test Status"
                            fields that could cause them to import and export improperly.
                        </li>
                        <li>
                            Modified the review table to display DHW COPs when a heat pump is modelled. Note this only
                            applies when "Heat Pump" or "Integrated Heat Pump" is selected under "Tank Type", since
                            "Tankless Heat Pumps" use EF as their efficiency input.
                        </li>
                        <li>
                            Added more robust error notices directly on fields and when exporting files, highlighting
                            issues that will produce calculation errors.
                        </li>
                        <li>
                            Changed compressed insulation warnings to highlight code fields with an exclamation rather
                            than produce a pop-up.
                        </li>
                        <li>
                            Added a warning when a user clicks to exit a model without saving (e.g. when the user clicks
                            to go to the dashboard).
                        </li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.4</h3>
                    <p>Added HOT2000 Version 11.11 Compatibility. Major changes include:</p>
                    <ul>
                        <li>Greener Homes fields added to Program section.</li>
                        <li>Changed "Unguarded" to "Guarded" in Airtightness section.</li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.3</h3>
                    <p>General bug fixes and improvements, including:</p>
                    <ul>
                        <li>Fixed a bug that sometimes caused polygons and their components to duplicate.</li>
                        <li>
                            Fixed a bug that would prevent the Actions pane from properly clearing, making it appear as
                            though a polygon's information was lost.
                        </li>
                        <li>Fixed a bug that could cause unassigned scales to become inaccessible.</li>
                        <li>
                            Fixed a bug that caused ceiling R-values for the following three Framing Cavity Insulation
                            Layer options to underestimate nominal R-values:
                            <ul>
                                <li>RSI 7.04 @ 300 mm (R 40 @ 11.8") batt</li>
                                <li>RSI 6.16 @ 267 mm (R 35 @ 10.5") batt</li>
                                <li>RSI 10.57 (R 60) Blown cellulose</li>
                            </ul>
                        </li>
                        <li>
                            Added the option "8 / 12" to the ceiling slope field. Note that in HOT2000, this will be
                            represented as a User Specified field with a value of 0.667.
                        </li>
                        <li>Converted Weather Region and Weather Location into searchable fields.</li>
                        <li>
                            Updated floor headers' default perimeter to be equal to the perimeter of the parent
                            component.
                        </li>
                        <li>
                            Adjusted values shown in enclosure component collapsed views and added tooltips (hover state
                            information).
                        </li>
                        <li>Added collapsed view information to all foundation components.</li>
                        <li>Changed Presence of Vermiculite field to No Vermiculite by default.</li>
                        <li>Fixed a bug that would sometimes cause lintel codes to produce corrupt files.</li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.2</h3>
                    <p>General bug fixes and improvements, including:</p>
                    <ul>
                        <li>
                            Removed the need to "Set" drawing components after making changes. Note that you still have
                            to click "Set Anchors" when you have finished creating a polygon component, and you still
                            have to save your drawing before exiting.
                        </li>
                        <li>Clicking "Page" in the elements pane now moves the view to that page of the drawing.</li>
                        <li>Minor iconography updates.</li>
                        <li>
                            Fixed a bug that sometimes caused the "Window Type" field in the drawing view to appear
                            blank.
                        </li>
                        <li>Fixed a bug that sometimes caused duplicate window components to appear.</li>
                        <li>Fixed a bug that caused the polygon tool to toggle off after every anchor drop.</li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.1</h3>
                    <p>General bug fixes and improvements, including:</p>
                    <ul>
                        <li>
                            Drawing close button now warns users before exiting to prevent unintentionally exiting the
                            drawing capture tool without saving changes.
                        </li>
                        <li>
                            Drawing tools now toggle off after each use to prevent users from unintentionally adding
                            multiple lines, windows, etc.
                        </li>
                        <li>
                            Default building components (Basement - 1, Ceiling - 1, Wall - 1, Floor - 1) are no longer
                            automatically populated when creating a new file.
                        </li>
                        <li>
                            Airtightness tab updates: wording altered for clarity, window airtightness fields moved to
                            "Other Factors" accordion, depressurization test status now allows inputs below 0.
                        </li>
                        <li>
                            Fixed an issue that would cause some window code fields (fill type and spacer type) to reset
                            to their default values when editing a window code.
                        </li>
                        <li>
                            Decimal requirements for some fields altered to be consistent with value imported from
                            HOT2000.
                        </li>
                    </ul>
                </div>
                <div className={classes.row}>
                    <h3>Update 0.1.0</h3>
                    <p>
                        This is our first live deployment, providing access to our project partners for user testing.
                        This deployment includes:
                    </p>
                    <ul>
                        <li>The core modelling interface</li>
                        <li>The ability to import and export .h2k files</li>
                        <li>The Drawing Capture tool</li>
                        <li>Compatibility with HOT2000 versions 11.9 and 11.10</li>
                        <li>Calculator drawers for heated floor areas and house volume</li>
                        <li>The integration of the Energy Star database for applicable mechanical systems</li>
                        <li>The Ontario Building Code compliance summary table</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};
